import { Slot } from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes, ReactElement } from 'react';

import { cn } from '../lib/cn.js';

import { actionButtonClass, type ActionButtonStyle } from './action-button.js';

// The Button, on the Cartel pill (`nu-btn`): an outline in the
// action's colour — teal is an accent, never a fill. The variant names are kept
// so existing call sites don't change; what each one means now:
//   primary · accent        teal outline          the screen's main action
//   secondary · outline ·
//   neutral                 ink outline           everything else, "Cancelar"
//   ghost                   ink, no border        the quietest action
//   danger                  amber, filled         a destructive confirm
// One Button for the dashboard and admin.

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'neutral'
  | 'danger'
  | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'pill';

const VARIANT: Readonly<Record<ButtonVariant, ActionButtonStyle>> = {
  primary: { tone: 'teal' },
  accent: { tone: 'teal' },
  secondary: { tone: 'ink' },
  outline: { tone: 'ink' },
  neutral: { tone: 'ink' },
  ghost: { tone: 'ink', bare: true },
  danger: { tone: 'amber', solid: true },
};

export const buttonVariants = ({
  variant = 'primary',
  size = 'md',
}: {
  variant?: ButtonVariant | null | undefined;
  size?: ButtonSize | null | undefined;
} = {}): string =>
  actionButtonClass({
    ...VARIANT[variant ?? 'primary'],
    size: size === 'pill' || size === null ? 'md' : size,
  });

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | null | undefined;
  readonly size?: ButtonSize | null | undefined;
  readonly asChild?: boolean;
}

export const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps): ReactElement => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
};
